import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Building2,
  Building,
  Users,
  Home,
  UserCheck,
  Shield,
  Settings,
  LogOut,
  Menu,
  X,
  BarChart3,
  UserCog,
  Activity,
  Key,
  MapPin,
  CreditCard,
  Factory,
  Search,
  Bell,
  ChevronDown,
  ChevronRight,
  FileText,
  TrendingUp,
  FileSearch,
  HelpCircle,
  Book,
  Monitor,
  Plus,
  Edit,
  Car,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Eye,
  ArrowLeft,
  Layout as LayoutIcon,
  Cpu,
  Briefcase,
  Smartphone,
  Package,
  UserPlus,
  Wrench,
  Layers,
  BookOpen,
  Minus,
  Square,
  Loader2,
  XCircle,
  Zap,
  ArrowUpCircle,
  ArrowDownCircle,
  ArrowLeftRight,
  Upload,
  CheckCircle2,
  Star,
  Download,
  LayoutGrid,
  // New icons for unique navigation
  ClipboardCheck,
  BadgeCheck,
  Lock,
  Cog,
  SquareStack,
  Boxes,
  Sliders,
  ShieldCheck,
  ShieldAlert,
  FolderOpen,
  Network,
  Rss,
  FolderTree,
  ScanLine,
  ScanFace,
  Contact,
  Radar,
  LayoutDashboard,
  ClipboardList,
  PawPrint,
  CornerDownLeft,
  RefreshCw,
  RotateCcw,
  Clock,
  Megaphone,
  Pin,
  PinOff,
  Tag,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useKeycloak } from '../contexts/KeycloakContext';
import { useCustomer } from '../contexts/CustomerContext';
import { useCustomerFilter } from '../contexts/CustomerFilterContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePersonDetailContext } from '../contexts/PersonDetailContext';
import { Button } from './ui/Button';
import { cn } from '../utils/cn';
import CustomerFilter from './CustomerFilter';
import GlobalSearch from './GlobalSearch';
import OperationalView from './OperationalView';
import StatusTooltip from './StatusTooltip';
import { ConnectionStatusIndicator } from './ConnectionStatusIndicator';
import { getUserRoleCategory, RoleCategory, formatRoleName } from '../config/roles';
import { getNavigationForUser } from '../config/navigation/index';
import { canAdministerAppVersions } from '../pages/appVersionPolicy';
import { fetchWithAuth, makeAbsolutePhotoUrl } from '../services/api';
import { deviceCommunicationService } from '../services/deviceCommunication';
import { triggerDeviceDoor } from '../utils/deviceDoorTrigger';
import { useDeviceHealthCheck } from '../hooks/useDeviceHealthCheck';
import { IoTDevice } from '../pages/ZonesDevices';
import { logger } from '../services/logger';
import { useNotificationPreferences } from '../hooks/useNotificationPreferences';
import toast from 'react-hot-toast';
import { initializeSmartNotifications, notify } from '../utils/smartNotification';
import { resolvePersonPhoto } from '../utils/personPhotoCache';
import { useEventStream } from '../hooks/useEventStream';
import { isElectron, isMac, isWindows, windowControls } from '../utils/platform';
import { useUserProfilePicture } from '../hooks/useUserProfilePicture';
import { PersonalSettingsModal } from './PersonalSettingsModal';
import { useVisitorExitAutoRevoke } from '../hooks/useVisitorExitAutoRevoke';
import { useCustomerDevicesAndZones } from '../hooks/useCustomerDevicesAndZones';

// Sidebar defaults to fixed/expanded on desktop unless the user has explicitly
// unpinned it before (an explicit 'false' in localStorage) — first-time users and
// anyone who cleared their preference get a fixed navbar, not a hover-to-expand one.
const getDefaultSidebarPinned = () => localStorage.getItem('sidebarPinned') !== 'false';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(getDefaultSidebarPinned);
  const [sidebarExpanded, setSidebarExpanded] = useState(getDefaultSidebarPinned);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [botoeiraOpen, setBotoeiraOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [personalSettingsOpen, setPersonalSettingsOpen] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isOperationalMode, setIsOperationalMode] = useState(() => {
    const saved = localStorage.getItem('isOperationalMode');
    return saved === 'true';
  });
  const [isSindicoMode, setIsSindicoMode] = useState(() => {
    return localStorage.getItem('isSindicoMode') === 'true';
  });
  const [isMoradorMode, setIsMoradorMode] = useState(() => {
    return localStorage.getItem('isMoradorMode') === 'true';
  });
  const [isFuncionarioMode, setIsFuncionarioMode] = useState(() => {
    return localStorage.getItem('isFuncionarioMode') === 'true';
  });
  const [operationalMenuOpen, setOperationalMenuOpen] = useState(false);
  const [contextSwitcherOpen, setContextSwitcherOpen] = useState(false);
  const [currentContext, setCurrentContext] = useState<'todos' | 'customer'>('customer');
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [bookmarkedCustomers, setBookmarkedCustomers] = useState<string[] | null>(null);
  const bookmarksLoadedRef = useRef(false);
  const bookmarksInitialLoadDone = useRef(false);
  const contextSwitcherTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sidebarTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const customerSearchInputRef = useRef<HTMLInputElement>(null);

  // Botoeira state for real devices
  const [botoeiraDevices, setBotoeiraDevices] = useState<Array<{
    id: string;
    name: string;
    device_type: string;
    status: string;
    zone_name?: string;
    zone_id?: string;
    zone_direction?: string;
    ip_address?: string;
    local_ip_address?: string;
    username?: string;
    password?: string;
    port?: number;
    manufacturer?: string;
    config?: Record<string, any>;
  }>>([]);
  const [botoeiraDeviceStates, setBotoeiraDeviceStates] = useState<Record<string, 'idle' | 'loading' | 'success' | 'error'>>({});
  const [botoeiraRestartStates, setBotoeiraRestartStates] = useState<Record<string, 'loading' | 'success' | 'error'>>({});
  const [botoeiraTimeStates, setBotoeiraTimeStates] = useState<Record<string, 'loading' | 'success' | 'error'>>({});
  const [botoeiraHoldProgress, setBotoeiraHoldProgress] = useState<Record<string, number>>({});
  const botoeiraHoldIntervalRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const botoeiraHoldStartRef = useRef<Record<string, number>>({});
  const [isLoadingBotoeiraDevices, setIsLoadingBotoeiraDevices] = useState(false);

  // Real-time device health check (same as ZonesDevices page)
  const { healthMap: botoeiraHealthMap } = useDeviceHealthCheck(botoeiraDevices as any as IoTDevice[], { interval: 30000 });

  const [expandedGroups, setExpandedGroups] = useState<string[]>(() => {
    const saved = localStorage.getItem('expandedGroups');
    return saved ? JSON.parse(saved) : ['Visão Geral', 'Clientes', 'Pessoas'];
  });

  // Track window maximized/fullscreen state for macOS traffic lights spacing
  const [isWindowMaximized, setIsWindowMaximized] = useState(false);

  const { hasPermission } = useAuth();
  const { user, logout: keycloakLogout, roles, initialized, authenticated, token, getRolesForCustomer } = useKeycloak();
  const { selectedCustomer, isUserBound, availableCustomers, selectCustomer, operationalRoles, activeRole, openRolePicker } = useCustomer();

  // Background visitor exit watcher — auto-revokes access when a visitor exits
  // Works in all views (redundant layer on top of OperationalView's own detection)
  const { handleEvent: handleVisitorEvent } = useVisitorExitAutoRevoke(selectedCustomer?.id);


  // Listen for Electron update-available broadcasts from UpdateStatusListener
  useEffect(() => {
    const onAvailable = () => setUpdateAvailable(true);
    const onCleared = () => setUpdateAvailable(false);
    window.addEventListener('panther:update-available', onAvailable);
    window.addEventListener('panther:update-cleared', onCleared);
    return () => {
      window.removeEventListener('panther:update-available', onAvailable);
      window.removeEventListener('panther:update-cleared', onCleared);
    };
  }, []);

  // Sync currentContext with selectedCustomer
  useEffect(() => {
    if (selectedCustomer) {
      console.log('[Layout] Syncing currentContext to customer:', selectedCustomer.name);
      setCurrentContext('customer');
    } else {
      console.log('[Layout] Syncing currentContext to todos (general view)');
      setCurrentContext('todos');
    }
  }, [selectedCustomer]);

  // Track window maximized/fullscreen state for macOS traffic lights spacing
  useEffect(() => {
    if (!isElectron() || !isMac()) return;

    const checkWindowState = async () => {
      const maximized = await windowControls.isMaximized();
      console.log('[Layout] Window state check:', { maximized, currentState: isWindowMaximized });
      setIsWindowMaximized(maximized);
    };

    // Check initial state
    checkWindowState();

    // Listen for window resize/maximize events
    const handleResize = () => {
      checkWindowState();
    };

    window.addEventListener('resize', handleResize);

    // Listen for fullscreen state changes from Electron via IPC
    const removeWindowStateListener = windowControls.onStateChanged(() => {
      console.log('[Layout] Window state changed (IPC event)');
      checkWindowState();
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      removeWindowStateListener();
    };
  }, []);

  const { selectedCustomers, setSelectedCustomers, selectedTypes, setSelectedTypes } = useCustomerFilter();
  const { preferences, shouldShowNotification } = useNotificationPreferences();
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAllNotifications } = useNotification();
  const { openPersonDetail } = usePersonDetailContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Get user profile picture with React Query caching (prevents duplicate fetches)
  const { profilePicture, invalidateProfilePicture } = useUserProfilePicture();

  // Get role-based navigation with customer context
  // IMPORTANT: When a customer is selected, use customer-specific roles instead of global roles
  // This ensures that users with customer-scoped roles (e.g., sindico for a specific customer)
  // see the correct navigation items based on their customer role, not their global role
  const effectiveRoles = selectedCustomer
    ? getRolesForCustomer(selectedCustomer.id)
    : roles;

  // If no customer-specific roles found, fall back to global roles
  const rolesToUse = effectiveRoles.length > 0 ? effectiveRoles : roles;

  // Check if the CURRENTLY ACTIVE profile is a porteiro (should be locked in operational
  // mode). Deliberately scoped to rolesToUse (the selected customer's roles, or activeRole
  // for a DB-only "Atribuições" assignment) rather than the raw global hasAnyRole('porteiro')
  // — a user who holds a real Keycloak porteiro GROUP role for one customer and a sindico
  // group role for another has 'porteiro' in their flat global roles array permanently,
  // regardless of which profile they've picked via "Trocar Perfil". Using the global check
  // would make isPorteiro stuck true forever for such a user, since it can't reflect which
  // customer/role is actually active right now — exactly why switching porteiro -> sindico
  // silently failed to leave operational mode while switching the other way "worked" (it
  // only ever needed isPorteiro to become true, which it already was).
  const isPorteiro = rolesToUse.includes('porteiro') || activeRole === 'porteiro';
  const isSindico = rolesToUse.includes('sindico') || activeRole === 'sindico';

  // DEBUG: Log role determination (disabled - enable via Personal Settings → Log Level: Debug)
  // console.log('[Layout] Role Debug:', {
  //   selectedCustomer: selectedCustomer?.name,
  //   selectedCustomerId: selectedCustomer?.id,
  //   globalRoles: roles,
  //   customerSpecificRoles: effectiveRoles,
  //   rolesToUse,
  //   roleCategory: getUserRoleCategory(rolesToUse)
  // });

  const effectiveRolesToUse = isSindicoMode
    ? ['sindico']
    : isMoradorMode
    ? ['morador']
    : isFuncionarioMode
    ? ['porteiro']
    : rolesToUse;
  const roleCategory = getUserRoleCategory(effectiveRolesToUse);
  const roleBasedNavigationForRole = getNavigationForUser(roleCategory, selectedCustomer, isUserBound, effectiveRolesToUse);
  // App-version floors reach every installed phone, so that entry is hidden from everyone but
  // the one person allowed to move them. The page guards itself too; this only stops a menu
  // item that 95% of admins would click and be refused. See pages/appVersionPolicy.ts.
  const roleBasedNavigation = canAdministerAppVersions(user)
    ? roleBasedNavigationForRole
    : roleBasedNavigationForRole
        .map(group => ({ ...group, items: group.items.filter(item => item.href !== '/app-versions') }))
        .filter(group => group.items.length > 0);

  // Listen for profile picture updates (e.g., when user changes their picture in Profile page)
  useEffect(() => {
    const handleProfilePictureUpdate = () => {
      logger.info('layout.component', 'Profile picture update event received, invalidating cache...');
      invalidateProfilePicture();
    };

    window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate);

    return () => {
      window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate);
    };
  }, [invalidateProfilePicture]);

  // Global keyboard shortcut for search (Cmd+K or Ctrl+K) - must be before any returns
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Save expanded groups to localStorage - must be before any returns
  useEffect(() => {
    localStorage.setItem('expandedGroups', JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  // Save operational mode to localStorage - must be before any returns
  useEffect(() => {
    localStorage.setItem('isOperationalMode', isOperationalMode.toString());
  }, [isOperationalMode]);

  // Deep-link support: other pages (e.g. PetDetailModal's "Unidade" click) can stash a
  // pending unit in localStorage and navigate here — enter operational mode so OperationalView
  // mounts and picks the pending unit up itself (see its own pending-unit effect).
  useEffect(() => {
    if (!isOperationalMode && localStorage.getItem('panther_operational_pending_unit')) {
      setIsOperationalMode(true);
    }
  }, [location.pathname, isOperationalMode]);

  // Save síndico mode to localStorage
  useEffect(() => {
    localStorage.setItem('isSindicoMode', isSindicoMode.toString());
  }, [isSindicoMode]);

  useEffect(() => {
    localStorage.setItem('isMoradorMode', isMoradorMode.toString());
  }, [isMoradorMode]);

  useEffect(() => {
    localStorage.setItem('isFuncionarioMode', isFuncionarioMode.toString());
  }, [isFuncionarioMode]);

  // Force operational mode for porteiros - they cannot exit operational mode.
  //
  // The "forced by porteiro" marker is kept in sync unconditionally (not only when this
  // effect is the one flipping isOperationalMode false->true) — otherwise a user who was
  // already in operational mode before this marker existed (isOperationalMode already
  // true at mount, so the transition below never happens) would never get the marker set
  // at all, and the exit-effect right below would never fire for them either.
  useEffect(() => {
    if (authenticated && isPorteiro) {
      localStorage.setItem('operationalModeForcedByPorteiro', 'true');
      if (!isOperationalMode) {
        logger.info('layout.porteiro', 'Forcing operational mode for porteiro user');
        setIsOperationalMode(true);
      }
    }
  }, [authenticated, isPorteiro, isOperationalMode]);

  // If operational mode was force-locked because the user was porteiro, and the active
  // role later changes away from porteiro (e.g. via "Trocar Perfil" — porteiro at one
  // customer, sindico at another), automatically exit operational mode too. Without this,
  // isOperationalMode stays true (persisted in localStorage from the porteiro lock above)
  // and traps the sindico/other role inside OperationalView, since nothing else ever
  // turns it back off. Gated on the dedicated "forced by porteiro" marker (not just
  // "!isPorteiro && isOperationalMode") so this never undoes a non-porteiro's own manual
  // "entrar em modo operacional" toggle.
  useEffect(() => {
    if (!isPorteiro && isOperationalMode && localStorage.getItem('operationalModeForcedByPorteiro') === 'true') {
      logger.info('layout.porteiro', 'Active role changed away from porteiro — exiting forced operational mode');
      localStorage.setItem('operationalModeForcedByPorteiro', 'false');
      setIsOperationalMode(false);
    }
  }, [isPorteiro, isOperationalMode]);

  // Load bookmarked customers from localStorage per user (only once)
  useEffect(() => {
    console.log('[Bookmarks] Load effect triggered', {
      authenticated,
      username: user?.username,
      initialized,
      alreadyLoaded: bookmarksInitialLoadDone.current
    });

    if (initialized && authenticated && user?.username && !bookmarksInitialLoadDone.current) {
      const storageKey = `customer_bookmarks_${user.username}`;
      const saved = localStorage.getItem(storageKey);
      console.log('[Bookmarks] Loading bookmarks for user:', user.username, 'saved data:', saved);

      if (saved) {
        try {
          const bookmarks = JSON.parse(saved);
          if (Array.isArray(bookmarks)) {
            console.log('[Bookmarks]  Loaded', bookmarks.length, 'bookmarks:', bookmarks);
            setBookmarkedCustomers(bookmarks);
          } else {
            console.warn('[Bookmarks] Saved data is not an array, resetting');
            setBookmarkedCustomers([]);
          }
        } catch (e) {
          console.error('[Bookmarks]  Failed to parse:', e);
          setBookmarkedCustomers([]);
        }
      } else {
        console.log('[Bookmarks] No saved bookmarks found, starting fresh');
        setBookmarkedCustomers([]);
      }
      bookmarksLoadedRef.current = true;
      bookmarksInitialLoadDone.current = true;
      console.log('[Bookmarks] Initial load complete, flags set');
    } else {
      console.log('[Bookmarks] Skipping load - waiting for initialization or already loaded');
    }
  }, [initialized, authenticated, user?.username]);

  // Save bookmarked customers to localStorage (only after initial load completes)
  useEffect(() => {
    console.log('[Bookmarks] Save effect triggered', {
      authenticated,
      username: user?.username,
      loaded: bookmarksLoadedRef.current,
      initialLoadDone: bookmarksInitialLoadDone.current,
      isNull: bookmarkedCustomers === null,
      count: bookmarkedCustomers?.length,
      bookmarks: bookmarkedCustomers
    });

    if (authenticated && user?.username && bookmarksLoadedRef.current && bookmarksInitialLoadDone.current && bookmarkedCustomers !== null) {
      const storageKey = `customer_bookmarks_${user.username}`;
      console.log('[Bookmarks]  Saving', bookmarkedCustomers.length, 'bookmarks for', user.username);
      localStorage.setItem(storageKey, JSON.stringify(bookmarkedCustomers));
      console.log('[Bookmarks]  Saved to localStorage');
    } else {
      console.log('[Bookmarks]  Skipping save - conditions not met');
    }
  }, [bookmarkedCustomers, authenticated, user?.username]);

  // Raw devices/zones for Botoeira — fetch is shared/de-duped across the app via
  // useCustomerDevicesAndZones (OperationalView's device list needs this same data and
  // used to trigger its own independent duplicate fetch of the same two endpoints).
  const { devices: rawBotoeiraDevices, zones: rawBotoeiraZones, isLoading: isLoadingRawBotoeiraDevices } = useCustomerDevicesAndZones(selectedCustomer?.id);

  useEffect(() => {
    setIsLoadingBotoeiraDevices(isLoadingRawBotoeiraDevices);
  }, [isLoadingRawBotoeiraDevices]);

  // Transform the raw API devices/zones into the Botoeira widget's shape whenever they change
  useEffect(() => {
    if (!selectedCustomer) {
      setBotoeiraDevices([]);
      return;
    }

    // Create a map of zone_id -> entry_type
    const zoneEntryTypeMap: Record<string, string> = {};
    rawBotoeiraZones.forEach((z: any) => {
      zoneEntryTypeMap[z.id] = z.entry_type;
    });

    setBotoeiraDevices(rawBotoeiraDevices.map((d: any) => {
      // Use zone_direction directly from API (comes from JOIN), fallback to map lookup
      const zoneDirection = d.zone_direction || (d.assigned_zone_id ? zoneEntryTypeMap[d.assigned_zone_id] : undefined);
      return {
        id: d.id,
        name: d.name,
        device_type: d.device_type,
        status: d.status || 'offline',
        zone_name: d.assigned_zone_name,
        zone_id: d.assigned_zone_id,
        zone_direction: zoneDirection,
        ip_address: d.ip_address,
        local_ip_address: d.local_ip_address,
        username: d.username,
        password: d.password,
        port: d.port,
        manufacturer: d.manufacturer,
        config: d.config,
      };
    }));
    logger.info('layout.botoeira', 'Loaded devices for Botoeira', {
      deviceCount: rawBotoeiraDevices.length,
      zoneCount: rawBotoeiraZones.length
    });
  }, [selectedCustomer, rawBotoeiraDevices, rawBotoeiraZones]);

  // Handle Botoeira device trigger action — delegates to shared triggerDeviceDoor utility
  const handleBotoeiraDeviceTrigger = async (device: typeof botoeiraDevices[number]) => {
    const { id: deviceId, name: deviceName, device_type: deviceType } = device;
    setBotoeiraDeviceStates(prev => ({ ...prev, [deviceId]: 'loading' }));

    try {
      await triggerDeviceDoor(device, fetchWithAuth);
      setBotoeiraDeviceStates(prev => ({ ...prev, [deviceId]: 'success' }));
      logger.info('layout.botoeira', 'Device triggered successfully', { deviceId, deviceName, deviceType });
    } catch (error) {
      setBotoeiraDeviceStates(prev => ({ ...prev, [deviceId]: 'error' }));
      logger.error('layout.botoeira', 'Error triggering device', error as Error);
      toast.error((error as Error)?.message || `Erro ao acionar ${deviceName}`);
    }

    // Reset state after 3 seconds
    setTimeout(() => {
      setBotoeiraDeviceStates(prev => {
        const newStates = { ...prev };
        delete newStates[deviceId];
        return newStates;
      });
    }, 3000);
  };

  const startBotoeiraHold = (deviceId: string, onComplete: () => void) => {
    botoeiraHoldStartRef.current[deviceId] = Date.now();
    botoeiraHoldIntervalRef.current[deviceId] = setInterval(() => {
      const p = Math.min(((Date.now() - botoeiraHoldStartRef.current[deviceId]) / 5000) * 100, 100);
      setBotoeiraHoldProgress(prev => ({ ...prev, [deviceId]: p }));
      if (p >= 100) {
        clearInterval(botoeiraHoldIntervalRef.current[deviceId]);
        setBotoeiraHoldProgress(prev => { const s = { ...prev }; delete s[deviceId]; return s; });
        onComplete();
      }
    }, 50);
  };

  const cancelBotoeiraHold = (deviceId: string) => {
    if (botoeiraHoldIntervalRef.current[deviceId]) {
      clearInterval(botoeiraHoldIntervalRef.current[deviceId]);
      delete botoeiraHoldIntervalRef.current[deviceId];
    }
    setBotoeiraHoldProgress(prev => { const s = { ...prev }; delete s[deviceId]; return s; });
  };

  const handleBotoeiraRestart = async (device: typeof botoeiraDevices[number]) => {
    const { id: deviceId, device_type: deviceType, manufacturer } = device;
    setBotoeiraRestartStates(prev => ({ ...prev, [deviceId]: 'loading' }));
    const isRFID = deviceType === 'rfid_reader' || deviceType === 'controlid_rfid';
    const isControlID = manufacturer?.toLowerCase().includes('control') || deviceType === 'controlid_face' || deviceType === 'controlid_rfid';
    const result = await deviceCommunicationService.rebootDevice({
      id: deviceId,
      ip_address: device.ip_address,
      local_ip_address: device.local_ip_address,
      username: device.username || '',
      password: device.password || '',
      vendor: isControlID ? 'controlid' : 'hikvision',
      port: device.port?.toString(),
    }, isRFID);
    setBotoeiraRestartStates(prev => ({ ...prev, [deviceId]: result.success ? 'success' : 'error' }));
    setTimeout(() => {
      setBotoeiraRestartStates(prev => { const s = { ...prev }; delete s[deviceId]; return s; });
    }, 3000);
  };

  const handleBotoeiraSetTime = async (device: typeof botoeiraDevices[number]) => {
    const { id: deviceId, device_type: deviceType, manufacturer } = device;
    setBotoeiraTimeStates(prev => ({ ...prev, [deviceId]: 'loading' }));
    const isRFID = deviceType === 'rfid_reader' || deviceType === 'controlid_rfid';
    const isControlID = manufacturer?.toLowerCase().includes('control') || deviceType === 'controlid_face' || deviceType === 'controlid_rfid';
    const result = await deviceCommunicationService.setDeviceTime({
      id: deviceId,
      ip_address: device.ip_address,
      local_ip_address: device.local_ip_address,
      username: device.username || '',
      password: device.password || '',
      vendor: isControlID ? 'controlid' : 'hikvision',
      port: device.port?.toString(),
    }, isRFID);
    setBotoeiraTimeStates(prev => ({ ...prev, [deviceId]: result.success ? 'success' : 'error' }));
    setTimeout(() => {
      setBotoeiraTimeStates(prev => { const s = { ...prev }; delete s[deviceId]; return s; });
    }, 3000);
  };

  // Deduplication: Track recently seen event IDs to prevent duplicate notifications
  // Uses a Map with event key -> timestamp to allow automatic cleanup of old entries
  const seenEventsRef = useRef<Map<string, number>>(new Map());
  const DEDUP_WINDOW_MS = 5000; // 5 second deduplication window

  // Global SSE connection for real-time notifications on ALL pages
  // Use useRef to create stable callback references that don't cause reconnections
  const handleAccessEvent = useCallback(async (event: any) => {
    console.log(' [Layout] Access event received:', event);

    // Skip door sensor events (mechanical, not authentication)
    if (event.event_type === 'door_opened' || event.event_type === 'door_closed') {
      console.log(' [Layout] Skipping door sensor event:', event.event_type);
      return;
    }

    // Skip internal state-machine events (same as OperationalView filter)
    if (event.event_type === 'auto_check_in' || event.event_type === 'auto_check_out') {
      return;
    }

    // Unauthorized/unregistered events are shown as non-clickable toasts if they have a name

    // DEDUPLICATION: Create a unique key for this event
    // Use combination of person_name + event_timestamp + device_id for deduplication
    // This catches duplicates even if the backend assigns different IDs
    const eventKey = `${event.person_name || ''}-${event.event_timestamp}-${event.device_id || ''}`;
    const now = Date.now();

    // Clean up old entries (older than DEDUP_WINDOW_MS)
    const seenEvents = seenEventsRef.current;
    Array.from(seenEvents.entries()).forEach(([key, timestamp]) => {
      if (now - timestamp > DEDUP_WINDOW_MS) {
        seenEvents.delete(key);
      }
    });

    // Check if we've seen this event recently
    if (seenEvents.has(eventKey)) {
      console.log(' [Layout] Skipping duplicate event:', eventKey);
      return;
    }

    // Mark this event as seen
    seenEvents.set(eventKey, now);
    console.log(' [Layout] Processing unique event:', eventKey);

    // Call notify.accessEvent to show toast AND add to notification center
    const isManualEvent = event.event_type === 'manual_check_in' || event.event_type === 'manual_check_out';

    // Backend doesn't always resolve a photo for RFID-badge events (only facial-match events
    // carry one inline). Fall back to the shared person-photo cache — a warm cache resolves
    // instantly; a cold cache is awaited briefly so the very first event for someone still
    // gets their photo instead of the fallback icon.
    let resolvedPhotoUrl = makeAbsolutePhotoUrl(event.face_photo_url) || undefined;
    if (!resolvedPhotoUrl && event.person_id) {
      resolvedPhotoUrl = await resolvePersonPhoto(event.person_id);
    }

    notify.accessEvent({
      personId: event.person_id,
      personName: event.person_name,
      deviceName: event.device_name,
      zoneName: event.zone_name,
      direction: event.direction, // 'entrada', 'saida', 'passagem'
      facePhotoUrl: resolvedPhotoUrl,
      eventType: event.event_type === 'remote_door_trigger' ? 'remote_door_trigger' : event.is_authorized ? 'authentication_success' : 'unauthorized',
      timestamp: new Date(event.event_timestamp),
      // Enriched fields
      customerName: event.customer_name,
      customerType: event.customer_type,
      unitName: event.unit_name,
      departmentName: event.department_name,
      personType: event.person_type,
      personRole: event.person_role,
      // RFID-specific fields
      vehicleId: (event as any).vehicle_id,
      vehiclePlate: event.vehicle_plate,
      accessType: event.access_type, // 'rfid' or undefined for facial
      isManual: isManualEvent,
    });
  }, []); // Empty deps - notify is a stable function

  const handleEventStreamError = useCallback((error: Error) => {
    console.error(' [Layout] SSE Error:', error);
    // Don't show error toast on every reconnection attempt
    // SSE will auto-reconnect with exponential backoff
  }, []);

  // Connect to event stream for real-time notifications + visitor auto check-in/out.
  // Always enabled so useVisitorExitAutoRevoke (handleVisitorEvent) gets SSE events in
  // ALL modes. In operational mode, OperationalView handles its own toasts via
  // useGlobalEventStream, so we skip handleAccessEvent there to avoid duplicate alerts.
  useEventStream({
    customerId: selectedCustomer?.id || '',
    token: token,
    enabled: !!selectedCustomer?.id && authenticated,
    onEvent: (event) => {
      if (!isOperationalMode) handleAccessEvent(event); // skip in operational — OperationalView handles those
      handleVisitorEvent(event);                        // visitor state machine (always active)
    },
    onError: handleEventStreamError
  });

  // Initialize smart notifications - must be before any returns
  useEffect(() => {
    initializeSmartNotifications(
      () => preferences,
      shouldShowNotification
    );
    logger.info('layout.component', 'Smart notifications initialized with user preferences');
  }, [preferences, shouldShowNotification]);

  // Listen for openUserModal event to open person detail modal (or navigate to unit for vehicle events)
  useEffect(() => {
    const handleOpenUserModal = async (event: CustomEvent) => {
      const { personId, vehicleId, vehiclePlate } = event.detail;
      if (personId) {
        console.log(' [Layout] Opening person detail modal for:', personId);
        openPersonDetail(personId, false, localStorage.getItem('isOperationalMode') === 'true');
      } else if ((vehicleId || vehiclePlate) && selectedCustomer?.id) {
        // Vehicle event — resolve unit via vehicle lookup then navigate to units page
        try {
          const url = vehicleId
            ? `/api/v1/customers/${selectedCustomer.id}/vehicles/${vehicleId}`
            : `/api/v1/customers/${selectedCustomer.id}/vehicles?search=${encodeURIComponent(vehiclePlate)}`;
          const res = await fetchWithAuth(url);
          if (res.ok) {
            const data = await res.json();
            const v = vehicleId ? data.data : (Array.isArray(data) ? data[0] : data.vehicles?.[0]);
            const unitId = v?.unit_id;
            if (unitId) {
              navigate(`/units?unit=${unitId}`);
            }
          }
        } catch { /* ignore */ }
      }
    };

    window.addEventListener('openUserModal', handleOpenUserModal as EventListener);

    return () => {
      window.removeEventListener('openUserModal', handleOpenUserModal as EventListener);
    };
  }, [openPersonDetail, selectedCustomer?.id, navigate]);

  // Focus search input when customer switcher opens
  useEffect(() => {
    if (contextSwitcherOpen) {
      setTimeout(() => customerSearchInputRef.current?.focus(), 50);
    }
  }, [contextSwitcherOpen]);

  // Debug operational mode access
  // Administrador role should NOT have access to operational mode (only Monitoramento)
  const isAdministrador = roles?.includes('administrador') ?? false;
  const canAccessOperationalMode = (roleCategory === RoleCategory.STAFF || roleCategory === RoleCategory.MANAGER) && !isAdministrador && !isSindico && !isSindicoMode;
  logger.debug('layout.component', 'Operational mode access check', {
    roleCategory,
    isStaff: roleCategory === RoleCategory.STAFF,
    isManager: roleCategory === RoleCategory.MANAGER,
    isAdministrador,
    canAccessOperationalMode,
  });

  // Debug: Log role information
  logger.debug('layout.component', 'Layout initialization state', {
    keycloakInitialized: initialized,
    keycloakAuthenticated: authenticated,
    userName: user?.name,
    userEmail: user?.email,
    rolesCount: roles?.length || 0,
    roles: roles || [],
    roleCategory,
    navigationGroupsCount: roleBasedNavigation.length,
    navigationGroups: roleBasedNavigation.map(g => g.group),
  });

  // Wait for authentication and roles to load
  if (!initialized || !authenticated) {
    logger.info('layout.component', 'Waiting for Keycloak initialization');
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // If user data or roles haven't loaded yet, wait
  if (!user || !roles || roles.length === 0) {
    logger.warn('layout.component', 'User or roles not loaded yet, waiting', {
      hasUser: !!user,
      rolesCount: roles?.length || 0,
    });
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando permissões...</p>
        </div>
      </div>
    );
  }

  // Map icon strings to icon components
  const iconMap: Record<string, React.ElementType> = {
    BarChart3, FileText, TrendingUp, Shield, Users, MapPin, CreditCard, Activity,
    Building2, Building, Home, UserCheck, Key, Settings, UserCog, Monitor, Plus,
    Edit, HelpCircle, Book, FileSearch, Bell, Cpu, Briefcase, Smartphone, Package,
    UserPlus, Wrench, Layers, BookOpen, Car, Upload, Download, LayoutGrid,
    // New unique navigation icons
    ClipboardCheck, BadgeCheck, Lock, Cog, SquareStack, Boxes, Sliders,
    ShieldCheck, ShieldAlert, FolderOpen, Network, Rss, FolderTree, ScanLine, ScanFace,
    Contact, Radar, LayoutDashboard, ClipboardList, PawPrint, Tag, AlertTriangle
  };

  // Convert role-based navigation to use icon components, filtering Electron-only items
  const inElectron = isElectron();
  const navigation = roleBasedNavigation.map(group => ({
    ...group,
    icon: iconMap[group.icon] || BarChart3,
    items: group.items
      ?.filter(item => !item.electronOnly || inElectron)
      .map(item => ({
        ...item,
        icon: iconMap[item.icon] || Users,
      }))
  }));

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupName)
        ? prev.filter(g => g !== groupName)
        : [...prev, groupName]
    );
  };

  // Toggle customer bookmark
  const toggleBookmark = (customerId: string) => {
    setBookmarkedCustomers(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  // Debug navigation
  logger.debug('layout.component', 'Navigation structure updated', {
    navigationCount: navigation.length,
    expandedGroups,
  });

  const handleLogout = () => {
    keycloakLogout();
  };

  // Handle context switching
  const handleContextSwitch = async (newContext: 'todos' | 'customer', customerId?: string) => {
    setCurrentContext(newContext);
    setContextSwitcherOpen(false);

    if (newContext === 'todos') {
      // Update context - DashboardRouter will handle the rest
      await selectCustomer(null);
      setSelectedCustomers([]);
      // Only navigate if not already on dashboard
      if (location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
    } else if (customerId) {
      // Update context - DashboardRouter will handle the rest
      const result = await selectCustomer(customerId);
      if (!result.success) {
        toast.error(result.error || 'Erro ao selecionar cliente');
        return;
      }
      setSelectedCustomers([customerId]);
      // Only navigate if not already on dashboard
      if (location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
    }
  };

  // Get current context display
  const getCurrentContextDisplay = () => {
    // Admins can see "Geral" option
    if (currentContext === 'todos' && roleCategory === RoleCategory.ADMIN) {
      return { icon: FileText, label: 'Geral', color: 'text-blue-600', subtitle: 'Visualização Geral', photoUrl: undefined as string | undefined };
    }

    if (selectedCustomer) {
      const icon = selectedCustomer.type === 'empresa' ? Building : Building2;
      const subtitle = selectedCustomer.type === 'empresa' ? 'Empresa' : 'Condomínio';
      return { icon, label: selectedCustomer.name, color: 'text-blue-600', subtitle, photoUrl: selectedCustomer.photo_url || undefined };
    }

    // Non-admins without customer should see prompt to select
    if (roleCategory !== RoleCategory.ADMIN) {
      return { icon: Building2, label: 'Selecione...', color: 'text-amber-600', subtitle: 'Selecione um cliente', photoUrl: undefined as string | undefined };
    }

    return { icon: Building2, label: 'Geral', color: 'text-gray-600', subtitle: '', photoUrl: undefined as string | undefined };
  };

  // Get current page name from navigation based on current path
  const getCurrentPageName = (): string => {
    const path = location.pathname;

    // Check direct href groups first
    for (const group of navigation) {
      if (group.href === path) {
        return group.group;
      }
      // Check items
      if (group.items) {
        for (const item of group.items) {
          if (item.href === path) {
            return item.name;
          }
          // Check subitems
          if (item.subItems) {
            for (const subItem of item.subItems) {
              if (subItem.href === path) {
                return subItem.name;
              }
            }
          }
        }
      }
    }

    // Fallback mappings for common routes
    const routeNames: Record<string, string> = {
      '/dashboard': 'Dashboard',
      '/global-dashboard': 'Dashboard Geral',
      '/residents': 'Moradores',
      '/visitors': 'Visitantes',
      '/liberados': 'Liberados',
      '/encomendas': 'Encomendas',
      '/employees': 'Funcionários',
      '/zones': 'Zonas',
      '/access-control': 'Controle de Acesso',
      '/eventos-gerais': 'Eventos Gerais',
      '/profile': 'Meu Perfil',
      '/notifications': 'Notificações',
      '/customers': 'Clientes',
      '/reports': 'Relatórios',
      '/global-reports': 'Relatórios Globais',
      '/units': 'Unidades',
      '/departments': 'Departamentos',
      '/vehicles': 'Veículos',
      '/suppliers': 'Fornecedores',
      '/deliveries': 'Entregas',
      '/common-areas': 'Áreas Comuns',
      '/access-profiles': 'Perfis de Acesso',
      '/access-rules': 'Regras de Acesso',
      '/audit-logs': 'Auditoria',
      '/device-audit': 'Audit de Dispositivos',
      '/user-administration': 'Administração',
      '/zones-devices': 'Zonas e Dispositivos',
      '/zone-groups': 'Grupos de Zonas',
      '/downloads': 'Downloads',
      '/avisos-atualizacoes': 'Avisos e Atualizações',
    };

    return routeNames[path] || 'Dashboard';
  };

  // Get dashboard icon based on role category
  const getDashboardIcon = () => {
    if (roleCategory === RoleCategory.ADMIN || roleCategory === RoleCategory.MANAGER) {
      return BarChart3;
    } else if (roleCategory === RoleCategory.STAFF) {
      return Activity;
    } else {
      return Home;
    }
  };

  const unreadCountDisplay = unreadCount > 100 ? '100+' : unreadCount.toString();

  // Render operational mode if enabled
  if (isOperationalMode) {
    return <OperationalView onExitOperationalMode={() => {
      // Porteiros cannot exit operational mode
      if (isPorteiro) {
        logger.warn('layout.porteiro', 'Porteiro attempted to exit operational mode - blocked');
        toast.error('Porteiros devem permanecer no modo operacional');
        return;
      }
      localStorage.setItem('operationalModeForcedByPorteiro', 'false');
      setIsOperationalMode(false);
    }} />;
  }

  return (
    <>
      {/* Global Search Modal */}
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      <div className="relative h-screen flex flex-col overflow-hidden bg-gray-50">
        {/* Electron-only: Top bar for macOS traffic lights with centered logo */}
        {isElectron() && (
          <div className={cn(
            "h-10 bg-black flex items-center justify-center flex-shrink-0 electron-drag relative z-50",
            // Conditional left padding based on window state
            // macOS fullscreen: pl-0 (no padding, traffic lights hidden)
            // macOS windowed/maximized: pl-20 (padding for traffic lights)
            isMac() ? (!isWindowMaximized ? "pl-20" : "pl-0") : ""
          )}>
            {/* Centered logo and text */}
            <div className="flex items-center gap-2 electron-no-drag">
              <img
                src={`${process.env.PUBLIC_URL || ''}/panther-logo.svg`}
                alt="Panther"
                className="h-6 w-auto"
              />
              <span className="text-white text-sm font-semibold tracking-wide">ACCESS</span>
            </div>
            {/* Windows window controls */}
            {isWindows() && (
              <div className="absolute right-2 flex items-center gap-0.5 electron-no-drag">
                <button
                  onClick={() => windowControls.minimize()}
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => windowControls.maximize()}
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded"
                >
                  <Square className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => windowControls.close()}
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-white hover:bg-red-600 rounded"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        )}

        <div className="flex-1 flex overflow-hidden">
        {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black bg-opacity-75" onClick={() => setSidebarOpen(false)}></div>
        <div className="fixed inset-y-0 left-0 flex w-80 flex-col">
          <SidebarContent
            navigation={navigation}
            location={location}
            navigate={navigate}
            user={user}
            profilePicture={profilePicture}
            handleLogout={handleLogout}
            setSidebarOpen={setSidebarOpen}
            expandedGroups={expandedGroups}
            toggleGroup={toggleGroup}
            userMenuOpen={userMenuOpen}
            personalSettingsOpen={personalSettingsOpen}
            setPersonalSettingsOpen={setPersonalSettingsOpen}
            setUserMenuOpen={setUserMenuOpen}
            onEnterOperationalMode={canAccessOperationalMode ? () => setIsOperationalMode(true) : undefined}
            hasAccess={availableCustomers.length > 0}
            updateAvailable={updateAvailable}
          />
        </div>
      </div>

      {/* Desktop sidebar - Collapsible on hover, or pinned open */}
      <div
        className={cn(
          "hidden lg:flex lg:flex-col lg:fixed lg:left-0 transition-all duration-300 ease-in-out z-40",
          isElectron() ? "lg:top-10 lg:bottom-0" : "lg:inset-y-0",
          sidebarExpanded ? "lg:w-64" : "lg:w-16"
        )}
        onMouseEnter={() => {
          if (sidebarPinned) return;
          if (sidebarTimeoutRef.current) clearTimeout(sidebarTimeoutRef.current);
          setSidebarExpanded(true);
        }}
        onMouseLeave={() => {
          if (sidebarPinned) return;
          sidebarTimeoutRef.current = setTimeout(() => {
            setSidebarExpanded(false);
          }, 200);
        }}
      >
        <SidebarContent
          navigation={navigation}
          location={location}
          navigate={navigate}
          user={user}
          profilePicture={profilePicture}
          handleLogout={handleLogout}
          expandedGroups={expandedGroups}
          toggleGroup={toggleGroup}
          userMenuOpen={userMenuOpen}
          setUserMenuOpen={setUserMenuOpen}
          personalSettingsOpen={personalSettingsOpen}
          setPersonalSettingsOpen={setPersonalSettingsOpen}
          onEnterOperationalMode={canAccessOperationalMode ? () => setIsOperationalMode(true) : undefined}
          collapsed={!sidebarExpanded}
          hasAccess={availableCustomers.length > 0}
          updateAvailable={updateAvailable}
          isPinned={sidebarPinned}
          onTogglePin={() => {
            const next = !sidebarPinned;
            setSidebarPinned(next);
            setSidebarExpanded(next);
            localStorage.setItem('sidebarPinned', String(next));
          }}
        />
      </div>

      {/* Main content */}
      <div className={cn(
        "flex flex-col flex-1 min-w-0 transition-all duration-300 ease-in-out",
        sidebarPinned ? "lg:pl-64" : "lg:pl-16"
      )}>
        {/* Top header - draggable in Electron for frameless window */}
        <header className="bg-white shadow-sm border-b border-gray-200 relative z-30">
          <div className="flex h-14 items-center px-4 gap-4">
            {/* ── Left: hamburger + page name ── */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <button
                type="button"
                className="lg:hidden -ml-0.5 h-9 w-9 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-black"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </button>

              {location.pathname !== '/dashboard' && (
                <span className="text-gray-300 text-sm hidden sm:block">|</span>
              )}
              {location.pathname !== '/dashboard' && (
                <span className="text-sm font-semibold text-gray-900 hidden sm:block truncate">
                  {getCurrentPageName()}
                </span>
              )}
            </div>

            {/* ── Center: customer display / context switcher + search ── */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Read-only customer display when NON-ADMIN user has exactly 1 customer */}
              {availableCustomers.length === 1 && selectedCustomer && roleCategory !== RoleCategory.ADMIN && (
                <div className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg">
                  {selectedCustomer.type === 'empresa' ? (
                    <Building className="h-4 w-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <Building2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  )}
                  <span className="text-sm font-medium text-gray-700">
                    {selectedCustomer.name}
                  </span>
                </div>
              )}

              {/* Trocar Perfil — shown only when the user holds more than one distinct
                  operational role (e.g. porteiro at one building, síndico at another).
                  Reopens the login-time role picker without restarting the app. */}
              {operationalRoles.length > 1 && (
                <button
                  onClick={openRolePicker}
                  title="Trocar perfil"
                  className="flex items-center gap-1.5 mr-3 px-2 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors border border-gray-200"
                >
                  <ArrowLeftRight className="h-3.5 w-3.5" />
                  <span>{activeRole ? formatRoleName(activeRole) : 'Trocar perfil'}</span>
                </button>
              )}

              {/* Context Switcher - Opens on Hover - Shown for admins (all counts) or non-admins with multiple customers */}
              {(availableCustomers.length > 1 || roleCategory === RoleCategory.ADMIN) ? (
              <div
                className="relative mr-4"
                onMouseEnter={() => {
                  if (contextSwitcherTimeoutRef.current) {
                    clearTimeout(contextSwitcherTimeoutRef.current);
                  }
                  setContextSwitcherOpen(true);
                }}
                onMouseLeave={() => {
                  contextSwitcherTimeoutRef.current = setTimeout(() => {
                    setContextSwitcherOpen(false);
                  }, 150);
                }}
              >
                {(() => {
                  const { icon: ContextIcon, label, color, subtitle, photoUrl } = getCurrentContextDisplay();
                  return (
                    <button
                      className={cn(
                        "flex items-center gap-2 px-2 py-0 rounded-lg hover:bg-gray-100 transition-colors",
                        contextSwitcherOpen && "bg-gray-100"
                      )}
                      title={subtitle}
                    >
                      {photoUrl ? (
                        <img src={makeAbsolutePhotoUrl(photoUrl)!} alt={label} className="h-9 w-9 rounded-lg object-cover ring-2 ring-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.35)] flex-shrink-0" />
                      ) : (
                        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center ring-2 ring-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.35)] flex-shrink-0">
                          <ContextIcon className="h-4 w-4 text-white" />
                        </div>
                      )}
                      <span className="text-sm font-medium text-gray-700">
                        {label}
                      </span>
                      <ChevronDown className={cn(
                        "h-3.5 w-3.5 text-gray-400 transition-transform",
                        contextSwitcherOpen && "rotate-180"
                      )} />
                    </button>
                  );
                })()}

                {/* Context Switcher Dropdown — always mounted so images stay in browser cache */}
                <div
                  className={cn(
                    "absolute top-full left-0 mt-1 min-w-[320px] max-w-[480px] bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[450px] overflow-hidden flex flex-col",
                    !contextSwitcherOpen && "hidden"
                  )}
                  onMouseEnter={() => {
                    if (contextSwitcherTimeoutRef.current) {
                      clearTimeout(contextSwitcherTimeoutRef.current);
                    }
                  }}
                  onMouseLeave={() => {
                    contextSwitcherTimeoutRef.current = setTimeout(() => {
                      setContextSwitcherOpen(false);
                    }, 150);
                  }}
                >
                    {/* Search Bar */}
                    <div className="p-1.5 border-b border-gray-200">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                        <input
                          ref={customerSearchInputRef}
                          type="text"
                          placeholder="Buscar..."
                          value={customerSearchQuery}
                          onChange={(e) => setCustomerSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                              const t = norm(customerSearchQuery);
                              const filtered = availableCustomers
                                .filter(c =>
                                  norm(c.name).includes(t) ||
                                  norm(c.city ?? '').includes(t) ||
                                  norm(c.type ?? '').includes(t)
                                );
                              if (filtered.length > 0) {
                                handleContextSwitch('customer', filtered[0].id);
                                setCustomerSearchQuery('');
                              }
                            }
                          }}
                          className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                        />
                      </div>
                    </div>

                    {/* Geral Option - Only for Admins */}
                    {roleCategory === RoleCategory.ADMIN && (
                      <div className="p-2 border-b border-gray-200">
                        <button
                          onClick={() => handleContextSwitch('todos')}
                          className={cn(
                            "w-full flex items-center gap-2 p-2 rounded-lg text-left transition-all",
                            currentContext === 'todos'
                              ? "bg-blue-600 text-white shadow-md"
                              : "hover:bg-gray-50 text-gray-900"
                          )}
                        >
                          <div className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                            currentContext === 'todos' ? "bg-white/20" : "bg-blue-100"
                          )}>
                            <FileText className={cn(
                              "h-4 w-4",
                              currentContext === 'todos' ? "text-white" : "text-blue-600"
                            )} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn(
                              "text-xs font-semibold",
                              currentContext === 'todos' ? "text-white" : "text-gray-900"
                            )}>
                              Geral
                            </p>
                            <p className={cn(
                              "text-[11px]",
                              currentContext === 'todos' ? "text-blue-100" : "text-gray-500"
                            )}>
                              Visualização geral
                            </p>
                          </div>
                          {currentContext === 'todos' && (
                            <CheckCircle className="h-4 w-4 text-white flex-shrink-0" />
                          )}
                        </button>
                      </div>
                    )}

                    {/* Customers List */}
                    <div className="overflow-y-auto flex-1 p-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
                      <div className="mb-2">
                        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2 mb-2">Clientes</h4>
                      </div>
                      {(() => {
                        const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                        const t = norm(customerSearchQuery);
                        const filteredCustomers = availableCustomers
                          .filter(customer =>
                            norm(customer.name).includes(t) ||
                            norm(customer.city ?? '').includes(t) ||
                            norm(customer.type ?? '').includes(t)
                          )
                          .sort((a, b) => {
                            // 1. Selected customer always first
                            const aIsSelected = selectedCustomer?.id === a.id;
                            const bIsSelected = selectedCustomer?.id === b.id;
                            if (aIsSelected && !bIsSelected) return -1;
                            if (!aIsSelected && bIsSelected) return 1;

                            // 2. Then bookmarked customers
                            const aBookmarked = bookmarkedCustomers?.includes(a.id) || false;
                            const bBookmarked = bookmarkedCustomers?.includes(b.id) || false;
                            if (aBookmarked && !bBookmarked) return -1;
                            if (!aBookmarked && bBookmarked) return 1;

                            // 3. Then by phase: production → implementation → disabled
                            const phaseOrder: Record<string, number> = { production: 0, implementation: 1, disabled: 2 };
                            const aPhase = phaseOrder[(a as any).phase || 'disabled'] ?? 2;
                            const bPhase = phaseOrder[(b as any).phase || 'disabled'] ?? 2;
                            if (aPhase !== bPhase) return aPhase - bPhase;

                            // 4. Then sort by name
                            return a.name.localeCompare(b.name);
                          });

                        if (filteredCustomers.length === 0) {
                          return (
                            <div className="p-4 text-center text-sm text-gray-500">
                              {customerSearchQuery ? 'Nenhum cliente encontrado' : 'Nenhum cliente disponível'}
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-1">
                            {filteredCustomers.map((customer, idx) => {
                              const isFirst = idx === 0;
                              const isSelected = currentContext === 'customer' && selectedCustomer?.id === customer.id;
                              const isBookmarked = bookmarkedCustomers?.includes(customer.id) || false;
                              const CustomerIcon = customer.type === 'empresa' ? Building : Building2;
                              const typeLabel = customer.type === 'empresa' ? 'Empresa' : 'Condomínio';
                              const typeColor = customer.type === 'empresa' ? 'text-green-600 bg-green-100' : 'text-blue-600 bg-blue-100';
                              const customerPhase = (customer as any).phase || 'disabled';
                              const phaseDotColor = customerPhase === 'production' ? 'bg-green-500' : customerPhase === 'implementation' ? 'bg-amber-400' : 'bg-gray-300';
                              const portariaType = (customer as any).portaria_type || 'none';
                              const portariaLabel = portariaType === 'presencial' ? 'Presencial' : portariaType === 'autonoma' ? 'Autônoma' : portariaType === 'virtual' ? 'Virtual' : null;
                              const portariaColorLight = portariaType === 'presencial' ? 'bg-blue-50 text-blue-700 border-blue-200' : portariaType === 'autonoma' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-teal-50 text-teal-700 border-teal-200';
                              const portariaColorDark = portariaType === 'presencial' ? 'bg-blue-500/15 text-blue-300 border-blue-500/25' : portariaType === 'autonoma' ? 'bg-blue-500/15 text-blue-300 border-blue-500/25' : 'bg-teal-500/15 text-teal-300 border-teal-500/25';

                              return (
                                <div
                                  key={customer.id}
                                  className="relative group"
                                >
                                  <button
                                    onClick={() => {
                                      handleContextSwitch('customer', customer.id);
                                      setCustomerSearchQuery('');
                                    }}
                                    className={cn(
                                      "w-full flex items-center gap-3 p-3 pr-10 rounded-lg text-left transition-all",
                                      isSelected
                                        ? "bg-black text-white shadow-md"
                                        : "hover:bg-gray-50 text-gray-900"
                                    )}
                                  >
                                  <div className="relative flex-shrink-0">
                                    <div className={cn(
                                      "w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden",
                                      isSelected ? "bg-white/20" : customer.type === 'empresa' ? 'bg-green-100' : 'bg-blue-100'
                                    )}>
                                      {customer.photo_url ? (
                                        <img src={makeAbsolutePhotoUrl(customer.photo_url)!} alt={customer.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                      ) : (
                                        <CustomerIcon className={cn(
                                          "h-5 w-5",
                                          isSelected ? "text-white" : customer.type === 'empresa' ? 'text-green-600' : 'text-blue-600'
                                        )} />
                                      )}
                                    </div>
                                    <span className={cn("absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2", isSelected ? "border-black" : "border-white", phaseDotColor)} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      {customer.internal_code && (
                                        <span className={cn(
                                          "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-bold flex-shrink-0",
                                          isSelected ? "bg-amber-400/20 text-amber-300 border border-amber-400/30" : "bg-amber-50 text-amber-700 border border-amber-200"
                                        )}>
                                          {customer.internal_code}
                                        </span>
                                      )}
                                      <p className={cn(
                                        "text-xs font-semibold truncate",
                                        isSelected ? "text-white" : "text-gray-900"
                                      )}>
                                        {customer.name}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <p className={cn(
                                        "text-[11px] truncate",
                                        isSelected ? "text-gray-300" : "text-gray-500"
                                      )}>
                                        {customer.city}
                                      </p>
                                      <span className={cn(
                                        "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium",
                                        isSelected ? "bg-white/20 text-white" : typeColor
                                      )}>
                                        {typeLabel}
                                      </span>
                                    </div>
                                    {portariaLabel && (
                                      <div className="mt-0.5">
                                        <span className={cn(
                                          "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border",
                                          isSelected ? portariaColorDark : portariaColorLight
                                        )}>
                                          {portariaLabel}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  {isSelected ? (
                                    <CheckCircle className="h-5 w-5 text-white flex-shrink-0" />
                                  ) : isFirst && customerSearchQuery ? (
                                    <CornerDownLeft className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                  ) : null}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleBookmark(customer.id);
                                  }}
                                  className={cn(
                                    "absolute right-2 top-2 p-1.5 rounded-md transition-all",
                                    isSelected
                                      ? isBookmarked
                                        ? "text-yellow-300 hover:bg-white/10"
                                        : "text-gray-300 hover:bg-white/10 opacity-0 group-hover:opacity-100"
                                      : isBookmarked
                                        ? "text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50"
                                        : "text-gray-400 hover:text-yellow-500 hover:bg-gray-100 opacity-0 group-hover:opacity-100"
                                  )}
                                  title={isBookmarked ? "Remover favorito" : "Adicionar aos favoritos"}
                                >
                                  <Star
                                    className={cn(
                                      "h-4 w-4",
                                      isBookmarked && "fill-current"
                                    )}
                                  />
                                </button>
                              </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
              </div>
              ) : null}

              {/* Search bar — centered alongside customer switcher */}
              {availableCustomers.length > 0 && (
                <>
                  <span className="text-gray-200 text-sm">|</span>
                  <button
                    onClick={() => setSearchOpen(true)}
                    className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 hover:border-gray-300 transition-all text-left group"
                  >
                    <Search className="h-4 w-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
                    <span className="text-sm text-gray-500 group-hover:text-gray-700 hidden sm:inline">
                      Buscar
                    </span>
                    <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 bg-white border border-gray-200 rounded">
                      {navigator.platform?.toLowerCase().includes('mac') ? '⌘' : 'Ctrl'}+K
                    </kbd>
                  </button>
                </>
              )}
            </div>

            {/* ── Right: icons ── */}
            <div className="flex items-center gap-2 flex-1 justify-end">
              {/* System status - dynamic indicator with tooltip */}
              <StatusTooltip>
                <div className="hidden md:block">
                  <ConnectionStatusIndicator />
                </div>
              </StatusTooltip>

              {/* Notifications */}
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-black rounded-lg transition-colors electron-no-drag"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full animate-pulse">
                    {unreadCountDisplay}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {notificationsOpen && (
                <>
                  <div className="fixed inset-0 z-40 electron-no-drag" onClick={() => setNotificationsOpen(false)} />
                  <div
                    className="fixed right-4 w-[420px] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[600px] flex flex-col overflow-hidden electron-no-drag"
                    style={{
                      top: isElectron() ? '104px' : '56px'
                    } as React.CSSProperties}
                  >
                      {/* Header */}
                      <div className="bg-white border-b border-gray-100 p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                              <Bell className="h-5 w-5 text-gray-700" />
                            </div>
                            <div>
                              <h3 className="text-sm font-bold text-gray-900">Central de Notificações</h3>
                              <p className="text-xs text-gray-500">
                                {unreadCount > 0 ? `${unreadCountDisplay} não lida${unreadCount > 1 ? 's' : ''}` : 'Todas lidas'}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setNotificationsOpen(false);
                            }}
                            className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg p-1.5 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 mt-3">
                          {notifications.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                clearAllNotifications();
                              }}
                              className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              Limpar Tudo
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Notifications List */}
                      <div className="overflow-y-auto flex-1 bg-gray-50 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                        {notifications.length === 0 ? (
                          <div className="p-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                              <Bell className="h-8 w-8 text-gray-400" />
                            </div>
                            <p className="text-sm font-medium text-gray-600">Nenhuma notificação</p>
                            <p className="text-xs text-gray-400 mt-1">Você está em dia!</p>
                          </div>
                        ) : (
                          <div className="p-2 space-y-1.5">
                            {notifications.map(notification => {
                              const borderColor = {
                                success: 'border-l-green-500',
                                error:   'border-l-red-500',
                                warning: 'border-l-yellow-500',
                                info:    'border-l-blue-500',
                              }[notification.type] ?? 'border-l-blue-500';

                              const fallbackIconColor = {
                                success: 'bg-green-500/10',
                                error:   'bg-red-500/10',
                                warning: 'bg-yellow-500/10',
                                info:    'bg-blue-500/10',
                              }[notification.type] ?? 'bg-blue-500/10';

                              const FallbackIcon = {
                                success: CheckCircle,
                                error:   AlertCircle,
                                warning: AlertTriangle,
                                info:    Bell,
                              }[notification.type] ?? Bell;

                              const fallbackIconText = {
                                success: 'text-green-500',
                                error:   'text-red-500',
                                warning: 'text-yellow-500',
                                info:    'text-blue-500',
                              }[notification.type] ?? 'text-blue-500';

                              const dir = (notification.metadata?.direction || '').toLowerCase();
                              const dirInfo = dir === 'entrada' || dir === 'entry'
                                ? { label: 'Entrada', bg: 'bg-green-100', text: 'text-green-700' }
                                : dir === 'saida' || dir === 'saída' || dir === 'exit'
                                  ? { label: 'Saída', bg: 'bg-blue-100', text: 'text-blue-700' }
                                  : dir === 'passagem' || dir === 'passage'
                                    ? { label: 'Passagem', bg: 'bg-blue-100', text: 'text-blue-700' }
                                    : null;

                              const location = notification.metadata?.departmentName || notification.metadata?.unitName || null;
                              // accessType === 'rfid' covers person tag/remote-control reads too, not just
                              // vehicles — only a real vehicle_plate means this is actually a vehicle event.
                              const isVehicle = !!notification.metadata?.vehiclePlate;

                              const openModal = (e: React.MouseEvent) => {
                                e.stopPropagation();
                                if (notification.metadata?.personId || notification.metadata?.personName) {
                                  markAsRead(notification.id);
                                  window.dispatchEvent(new CustomEvent('openUserModal', {
                                    detail: {
                                      personName:     notification.metadata.personName,
                                      personId:       notification.metadata.personId,
                                      facePhotoUrl:   notification.metadata.facePhotoUrl,
                                      customerName:   notification.metadata.customerName,
                                      unitName:       notification.metadata.unitName,
                                      departmentName: notification.metadata.departmentName,
                                      personRole:     notification.metadata.personRole,
                                    }
                                  }));
                                }
                              };

                              return (
                                <div
                                  key={notification.id}
                                  onClick={openModal}
                                  className={cn(
                                    'relative flex overflow-hidden rounded-lg border border-gray-200 border-l-4 transition-colors cursor-pointer',
                                    borderColor,
                                    notification.read
                                      ? 'bg-white hover:bg-gray-50'
                                      : 'bg-white hover:bg-gray-50 shadow-sm',
                                  )}
                                  style={{ minHeight: '80px' }}
                                >
                                  {/* Unread dot */}
                                  {!notification.read && (
                                    <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-indigo-500" />
                                  )}

                                  {/* Photo strip */}
                                  <div className="w-[68px] flex-shrink-0 relative bg-gray-100">
                                    {notification.metadata?.facePhotoUrl ? (
                                      <img
                                        src={notification.metadata.facePhotoUrl}
                                        alt={notification.metadata.personName || ''}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                      />
                                    ) : (
                                      <div className={cn('absolute inset-0 w-full h-full flex items-center justify-center', fallbackIconColor)}>
                                        {isVehicle
                                          ? <Car className="h-7 w-7 text-blue-500" />
                                          : <FallbackIcon className={cn('h-7 w-7', fallbackIconText)} />
                                        }
                                      </div>
                                    )}
                                  </div>

                                  {/* Content */}
                                  <div className="flex-1 min-w-0 p-2 pr-5">
                                    {/* Customer name */}
                                    {notification.metadata?.customerName && (
                                      <div className="flex items-center gap-1 mb-1 pb-1 border-b border-gray-100">
                                        <Building2 className="h-3 w-3 text-blue-500 flex-shrink-0" />
                                        <span className="text-[10px] font-medium text-blue-600 truncate">{notification.metadata.customerName}</span>
                                      </div>
                                    )}

                                    {/* Name */}
                                    <p className={cn(
                                      'text-sm font-semibold leading-snug truncate',
                                      notification.read ? 'text-gray-600' : 'text-gray-900',
                                    )}>
                                      {notification.metadata?.personName || notification.title}
                                    </p>

                                    {/* Message (when different from person name) */}
                                    {notification.message && notification.message !== (notification.metadata?.personName || '') && (
                                      <p className="text-xs text-gray-500 mt-0.5 truncate">{notification.message}</p>
                                    )}

                                    {/* Role + location */}
                                    {(notification.metadata?.personRole || location) && (
                                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                        {notification.metadata?.personRole && (
                                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded">
                                            {notification.metadata.personRole}
                                          </span>
                                        )}
                                        {location && (
                                          <span className="flex items-center gap-0.5 text-[10px] text-gray-500">
                                            <Home className="h-2.5 w-2.5 flex-shrink-0" />
                                            {location}
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Direction + zone + time */}
                                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                      {dirInfo && (
                                        <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold', dirInfo.bg, dirInfo.text)}>
                                          {dirInfo.label}
                                        </span>
                                      )}
                                      {notification.metadata?.zoneName && (
                                        <span className="flex items-center gap-0.5 text-[10px] text-gray-500">
                                          <MapPin className="h-2.5 w-2.5 text-gray-400 flex-shrink-0" />
                                          <span className="truncate max-w-[100px]">{notification.metadata.zoneName}</span>
                                        </span>
                                      )}
                                      {notification.metadata?.vehiclePlate && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">
                                          <Car className="h-2.5 w-2.5" />
                                          {notification.metadata.vehiclePlate}
                                        </span>
                                      )}
                                      <span className="text-[10px] text-gray-400 ml-auto whitespace-nowrap">
                                        {new Date(notification.timestamp).toLocaleString('pt-BR', {
                                          day: '2-digit', month: '2-digit',
                                          hour: '2-digit', minute: '2-digit',
                                        })}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      {notifications.length > 0 && (
                        <div className="border-t border-gray-100 p-3 bg-white">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              markAllAsRead();
                            }}
                            className="w-full px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            Marcar todas como lidas
                          </button>
                        </div>
                      )}

                      {/* Go to Notification Center Button */}
                      <div className="p-3 border-t border-gray-100 bg-white">
                        <button
                          onClick={() => {
                            setNotificationsOpen(false);
                            navigate('/notifications');
                          }}
                          className="w-full px-4 py-2 text-sm font-medium text-center text-white bg-gradient-to-r from-gray-800 to-black rounded-lg hover:from-gray-700 hover:to-gray-900 transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <Bell className="h-4 w-4" />
                          Ver Central de Notificações
                        </button>
                      </div>
                  </div>
                </>
              )}

            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden bg-gray-50 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="py-4 pb-20 h-full">
            <div className="max-w-[1920px] mx-auto px-4 lg:px-6 h-full">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
      {/* Modal portal mount point — portals render here (absolute positioning) instead of
       * document.body (position:fixed) to avoid SwiftShader compositing crashes on Windows. */}
      <div id="modal-root" />
      </div>

      {/* Botoeira Bottom Panel */}
      {botoeiraOpen && (
        <>
          {/* Click outside to close */}
          <div
            className="fixed inset-0 z-30"
            onClick={() => setBotoeiraOpen(false)}
            style={{ bottom: '32vh' }}
          />

          <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t-2 border-orange-500 shadow-2xl" style={{ height: '32vh' }}>
            {/* Header Bar */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <Activity className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-white">Botoeira</h2>
                  <p className="text-[10px] text-orange-100">Acionadores</p>
                </div>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setBotoeiraOpen(false);
                }}
                className="text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content - Horizontal Scrollable List */}
            <div className="h-[calc(100%-48px)] overflow-x-auto overflow-y-hidden bg-gray-50 p-3 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <div className="flex gap-2.5 h-full">
                {/* Loading State */}
                {isLoadingBotoeiraDevices && (
                  <div className="flex items-center justify-center w-full">
                    <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
                    <span className="ml-2 text-sm text-gray-500">Carregando dispositivos...</span>
                  </div>
                )}

                {/* No devices */}
                {!isLoadingBotoeiraDevices && botoeiraDevices.length === 0 && (
                  <div className="flex items-center justify-center w-full text-gray-500">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    <span className="text-sm">
                      {selectedCustomer ? 'Nenhum dispositivo encontrado para este cliente' : 'Selecione um cliente para ver os dispositivos'}
                    </span>
                  </div>
                )}

                {/* Real Devices List */}
                {!isLoadingBotoeiraDevices && botoeiraDevices.map((device, index) => {
                  const deviceState = botoeiraDeviceStates[device.id] || 'idle';

                  // Determine icon based on device type
                  const getDeviceIcon = () => {
                    switch (device.device_type) {
                      case 'facial_recognition':
                        return Users;
                      case 'rfid_reader':
                        return Car;
                      case 'intercom':
                        return Shield;
                      default:
                        return Cpu;
                    }
                  };
                  const Icon = getDeviceIcon();

                  // Get direction icon
                  const getDirectionIcon = () => {
                    switch (device.zone_direction) {
                      case 'entrada':
                        return ArrowDownCircle;
                      case 'saida':
                        return ArrowUpCircle;
                      case 'passagem':
                        return ArrowLeftRight;
                      default:
                        return null;
                    }
                  };
                  const DirectionIcon = getDirectionIcon();

                  // Get direction label
                  const getDirectionLabel = () => {
                    switch (device.zone_direction) {
                      case 'entrada':
                        return 'ENTRADA';
                      case 'saida':
                        return 'SAÍDA';
                      case 'passagem':
                        return 'PASSAGEM';
                      default:
                        return '';
                    }
                  };

                  // Determine color based on zone direction
                  const getColorClasses = () => {
                    if (deviceState === 'loading') return 'from-blue-500 to-blue-600';
                    if (deviceState === 'success') return 'from-green-500 to-green-600';
                    if (deviceState === 'error') return 'from-red-500 to-red-600';

                    // Color based on zone direction
                    switch (device.zone_direction) {
                      case 'entrada':
                        return 'from-green-500 to-green-600 hover:from-green-600 hover:to-green-700';
                      case 'saida':
                        return 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700';
                      case 'passagem':
                        return 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700';
                      default:
                        return 'from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700';
                    }
                  };

                  return (
                    <div key={device.id} className="flex-shrink-0 w-40 h-full flex flex-col gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (deviceState === 'idle') {
                          handleBotoeiraDeviceTrigger(device);
                        }
                      }}
                      disabled={deviceState !== 'idle'}
                      className={cn(
                        "flex-1 w-full bg-gradient-to-br text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 p-3 flex flex-col items-center justify-between group relative",
                        deviceState === 'idle' && "transform hover:scale-105",
                        deviceState !== 'idle' && "cursor-not-allowed",
                        getColorClasses()
                      )}
                    >
                      {/* Button ID Number - top right corner */}
                      <div className={cn(
                        "absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shadow-lg border-2",
                        deviceState === 'idle' && "bg-gray-900 text-gray-300 border-white/30",
                        deviceState !== 'idle' && "bg-white/90 text-gray-700 border-white"
                      )}>
                        {index + 1}
                      </div>

                      <div className="flex flex-col items-center gap-1.5 flex-1 justify-center">
                        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                          {deviceState === 'loading' && <Loader2 className="h-6 w-6 text-white animate-spin" />}
                          {deviceState === 'success' && <CheckCircle className="h-6 w-6 text-white" />}
                          {deviceState === 'error' && <XCircle className="h-6 w-6 text-white" />}
                          {deviceState === 'idle' && <Icon className="h-6 w-6 text-white" />}
                        </div>
                        <div className="text-center">
                          <p className="text-[11px] font-bold leading-tight">{device.name}</p>
                          {device.zone_name && (
                            <p className="text-[9px] opacity-80 mt-0.5">{device.zone_name}</p>
                          )}

                          {/* Zone Direction Flag - tiny badge below name */}
                          {deviceState === 'idle' && (
                            <div className={cn(
                              "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[7px] font-medium mt-1",
                              device.zone_direction === 'entrada' && "bg-green-500/20 text-green-300",
                              device.zone_direction === 'saida' && "bg-orange-500/20 text-orange-300",
                              device.zone_direction === 'passagem' && "bg-blue-500/20 text-blue-300",
                              !device.zone_direction && "bg-gray-500/20 text-gray-400"
                            )}>
                              <div className={cn(
                                "w-1 h-1 rounded-full",
                                device.zone_direction === 'entrada' && "bg-green-400",
                                device.zone_direction === 'saida' && "bg-orange-400",
                                device.zone_direction === 'passagem' && "bg-blue-400",
                                !device.zone_direction && "bg-gray-500"
                              )} />
                              <span className="uppercase">{getDirectionLabel() || 'Não Configurado'}</span>
                            </div>
                          )}
                          {(() => {
                            const health = botoeiraHealthMap.get(device.id);
                            const liveStatus = health?.status ?? (device.ip_address ? 'checking' : null);
                            if (!liveStatus) return null;
                            return (
                              <div className="flex items-center justify-center gap-1 mt-0.5">
                                {liveStatus === 'checking' ? (
                                  <Loader2 className="w-1.5 h-1.5 text-amber-300 animate-spin" />
                                ) : (
                                  <div className={cn(
                                    "w-1.5 h-1.5 rounded-full",
                                    liveStatus === 'online' ? "bg-green-300" : "bg-red-300"
                                  )} />
                                )}
                                <p className="text-[8px] opacity-70">
                                  {liveStatus === 'checking' ? 'Verificando...' : liveStatus === 'online' ? 'Online' : 'Offline'}
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="w-full pt-2 border-t border-white/20">
                        <div className="px-2.5 py-1 bg-white/20 hover:bg-white/30 rounded text-[10px] font-medium transition-colors flex items-center justify-center gap-1">
                          {deviceState === 'loading' && 'Acionando...'}
                          {deviceState === 'success' && 'Acionado!'}
                          {deviceState === 'error' && 'Erro'}
                          {deviceState === 'idle' && (
                            <>
                              <Zap className="h-3 w-3" />
                              Acionar
                            </>
                          )}
                        </div>
                      </div>
                    </button>
                    {/* Secondary actions */}
                    <div className="flex gap-1">
                      <button
                        onMouseDown={(e) => { e.stopPropagation(); if (!botoeiraRestartStates[device.id]) startBotoeiraHold(device.id, () => handleBotoeiraRestart(device)); }}
                        onMouseUp={(e) => { e.stopPropagation(); cancelBotoeiraHold(device.id); }}
                        onMouseLeave={() => cancelBotoeiraHold(device.id)}
                        onTouchStart={(e) => { e.stopPropagation(); if (!botoeiraRestartStates[device.id]) startBotoeiraHold(device.id, () => handleBotoeiraRestart(device)); }}
                        onTouchEnd={(e) => { e.stopPropagation(); cancelBotoeiraHold(device.id); }}
                        disabled={!!botoeiraRestartStates[device.id]}
                        title="Segure para reiniciar"
                        className="relative flex-1 flex items-center justify-center gap-1 py-1.5 bg-white border border-gray-200 rounded-lg text-[10px] text-orange-600 hover:bg-orange-50 transition-colors disabled:opacity-50 overflow-hidden select-none"
                      >
                        <div
                          className="absolute inset-y-0 left-0 bg-orange-200/60 transition-none"
                          style={{ width: `${botoeiraHoldProgress[device.id] ?? 0}%` }}
                        />
                        {botoeiraRestartStates[device.id] === 'loading'
                          ? <Loader2 className="w-3 h-3 animate-spin relative z-10" />
                          : botoeiraRestartStates[device.id] === 'success'
                            ? <CheckCircle className="w-3 h-3 text-green-500 relative z-10" />
                            : <RotateCcw className={cn("w-3 h-3 relative z-10", (botoeiraHoldProgress[device.id] ?? 0) > 0 && "animate-spin")} />}
                        <span className="relative z-10">Reiniciar</span>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleBotoeiraSetTime(device); }}
                        disabled={!!botoeiraTimeStates[device.id]}
                        title="Sincronizar horário BRT"
                        className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-white border border-gray-200 rounded-lg text-[10px] text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
                      >
                        {botoeiraTimeStates[device.id] === 'loading'
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : botoeiraTimeStates[device.id] === 'success'
                            ? <CheckCircle className="w-3 h-3 text-green-500" />
                            : <Clock className="w-3 h-3" />}
                        <span>Horário</span>
                      </button>
                    </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Toggle Button at Bottom Right - Only for STAFF and MANAGER */}
      {(roleCategory === RoleCategory.STAFF || roleCategory === RoleCategory.MANAGER) && (
        <button
          onClick={() => setBotoeiraOpen(!botoeiraOpen)}
          className={cn(
            "fixed bottom-4 right-6 z-50 flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 rounded-full",
            botoeiraOpen ? "mb-[32vh]" : "mb-0"
          )}
        >
          <Activity className="h-4 w-4" />
          <span className="text-xs font-bold">Botoeira</span>
          <span className="text-[10px]">
            {botoeiraOpen ? '▼' : '▲'}
          </span>
        </button>
      )}
      </div>
    </>
  );
};

interface SidebarContentProps {
  navigation: any[];
  location: any;
  navigate: any;
  user: any;
  profilePicture?: string | null;
  handleLogout: () => void;
  setSidebarOpen?: (open: boolean) => void;
  expandedGroups: string[];
  toggleGroup: (groupName: string) => void;
  userMenuOpen?: boolean;
  setUserMenuOpen?: (open: boolean) => void;
  personalSettingsOpen?: boolean;
  setPersonalSettingsOpen?: (open: boolean) => void;
  onEnterOperationalMode?: () => void;
  collapsed?: boolean;
  hasAccess?: boolean; // Whether user has access to any customer
  updateAvailable?: boolean;
  isPinned?: boolean;
  onTogglePin?: () => void;
}

const SidebarContent: React.FC<SidebarContentProps> = ({
  navigation,
  location,
  navigate,
  user,
  profilePicture,
  handleLogout,
  personalSettingsOpen,
  setPersonalSettingsOpen,
  setSidebarOpen,
  expandedGroups,
  toggleGroup,
  userMenuOpen = false,
  setUserMenuOpen,
  onEnterOperationalMode,
  collapsed = false,
  hasAccess = true,
  updateAvailable = false,
  isPinned = false,
  onTogglePin,
}) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [navSearch, setNavSearch] = useState('');
  const [hasMoreBelow, setHasMoreBelow] = useState(true);
  const navRef = useRef<HTMLDivElement>(null);
  const { operationalRoles, openRolePicker } = useCustomer();

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const check = () => setHasMoreBelow(nav.scrollTop + nav.clientHeight < nav.scrollHeight - 8);
    check();
    nav.addEventListener('scroll', check);
    window.addEventListener('resize', check);
    return () => { nav.removeEventListener('scroll', check); window.removeEventListener('resize', check); };
  }, [navigation]);

  // Accent-insensitive filter for the "search the menu" box below — matches on group,
  // item, and sub-item names so e.g. "areas" finds "Áreas Comuns".
  const normalizeForSearch = (s: string) =>
    (s || '')
      .toLowerCase()
      .replace(/[aàáâã]/g, 'a')
      .replace(/[eèéê]/g, 'e')
      .replace(/[iìí]/g, 'i')
      .replace(/[oòóôõ]/g, 'o')
      .replace(/[uùú]/g, 'u')
      .replace(/[ç]/g, 'c');
  const navSearchTerm = normalizeForSearch(navSearch.trim());
  const filteredNavigation = useMemo(() => {
    if (!navSearchTerm) return navigation;
    return navigation
      .map((group: any) => {
        if (group.href) {
          return normalizeForSearch(group.group || '').includes(navSearchTerm) ? group : null;
        }
        const items = (group.items || []).filter((item: any) => {
          const nameMatches = normalizeForSearch(item.name || '').includes(navSearchTerm);
          const subMatches = (item.subItems || []).some((sub: any) =>
            normalizeForSearch(sub.name || '').includes(navSearchTerm)
          );
          return nameMatches || subMatches;
        });
        return items.length > 0 ? { ...group, items } : null;
      })
      .filter(Boolean);
  }, [navigation, navSearchTerm]);

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-black to-gray-900 overflow-hidden">
      {/* Search the menu - always at the very top of the sidebar. Filters the nav
          items below live as you type; unrelated to the top bar's entity search. */}
      {hasAccess && !collapsed && (
        <div className="border-b border-gray-800 px-3 py-2">
          <div className="relative flex items-center">
            <Search className="absolute left-2.5 h-3.5 w-3.5 text-gray-500 pointer-events-none" />
            <input
              type="text"
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
              placeholder="Buscar no menu"
              className="w-full pl-8 pr-7 py-1.5 text-sm rounded-lg bg-gray-800/60 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            {navSearch && (
              <button
                onClick={() => setNavSearch('')}
                title="Limpar"
                className="absolute right-2 text-gray-500 hover:text-white"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Logo section - hidden in Electron since we have the top bar */}
      {!isElectron() && (
        <div className="bg-black border-b border-gray-800">
          <div className={cn("flex items-center h-14", collapsed ? "px-2 justify-center" : "px-4")}>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center focus:outline-none rounded-lg p-1"
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/panther-logo.svg`}
                alt="Panther Logo"
                className={cn("w-auto", collapsed ? "h-8" : "h-10")}
              />
              {!collapsed && (
                <div className="ml-3">
                  <h1 className="text-xl font-bold text-white hover:text-gray-300 active:text-white transition-colors duration-200" style={{ fontFamily: 'Montserrat, sans-serif' }}>ACCESS</h1>
                </div>
              )}
            </button>
            {setSidebarOpen && (
              <button
                onClick={() => setSidebarOpen(false)}
                className="ml-auto lg:hidden text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            )}
          </div>

          {/* Operational Mode Toggle - Only for STAFF and MANAGER roles */}
          {onEnterOperationalMode && !collapsed && (
            <div className="border-t border-gray-800 px-3">
              <button
                onClick={() => {
                  onEnterOperationalMode();
                  setSidebarOpen?.(false);
                }}
                className="w-full py-2 px-3 text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 rounded-lg mt-2 mb-2 flex items-center justify-between gap-2 group shadow-lg overflow-hidden"
                title="Entrar no Modo Operacional"
              >
                <div className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
                  <Eye className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium truncate">Modo Operacional</span>
                </div>
                <ChevronRight className="h-4 w-4 flex-shrink-0" />
              </button>
            </div>
          )}
          {onEnterOperationalMode && collapsed && (
            <div className="border-t border-gray-800 px-2 py-2">
              <button
                onClick={() => {
                  onEnterOperationalMode();
                  setSidebarOpen?.(false);
                }}
                className="w-full p-2 text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 rounded-lg flex items-center justify-center shadow-lg"
                title="Modo Operacional"
              >
                <Eye className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Electron: Operational Mode Toggle at the top of sidebar */}
      {isElectron() && onEnterOperationalMode && !collapsed && (
        <div className="bg-black border-b border-gray-800 px-3 py-2">
          <button
            onClick={() => {
              onEnterOperationalMode();
              setSidebarOpen?.(false);
            }}
            className="w-full py-2 px-3 text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 rounded-lg flex items-center justify-between gap-2 group shadow-lg overflow-hidden"
            title="Entrar no Modo Operacional"
          >
            <div className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
              <Eye className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm font-medium truncate">Modo Operacional</span>
            </div>
            <ChevronRight className="h-4 w-4 flex-shrink-0" />
          </button>
        </div>
      )}
      {isElectron() && onEnterOperationalMode && collapsed && (
        <div className="bg-black border-b border-gray-800 px-2 py-2">
          <button
            onClick={() => {
              onEnterOperationalMode();
              setSidebarOpen?.(false);
            }}
            className="w-full p-2 text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 rounded-lg flex items-center justify-center shadow-lg"
            title="Modo Operacional"
          >
            <Eye className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Navigation - Only show if user has access */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 relative overflow-hidden">
        <nav ref={navRef as React.RefObject<HTMLDivElement>} className={cn(
          "h-full space-y-1 overflow-y-auto py-4 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-gray-700/40 [&::-webkit-scrollbar-thumb]:bg-gray-500 [&::-webkit-scrollbar-thumb]:rounded-full",
          collapsed ? "px-2" : "px-3"
        )}>
          {hasAccess && navSearchTerm && filteredNavigation.length === 0 && (
            <p className="px-3 py-4 text-xs text-gray-500 text-center">Nenhum item encontrado</p>
          )}
          {hasAccess && filteredNavigation.map((group: any) => {
            const isGroupExpanded = true;
            const GroupIcon = group.icon;

            return (
              <div key={group.group}>
                {/* Direct link group (no subitems) */}
                {group.href ? (
                  <div className={cn("mb-1", collapsed ? "mt-1" : "mt-4 first:mt-1")}>
                    <button
                      onClick={() => {
                        navigate(group.href!);
                        setSidebarOpen?.(false);
                      }}
                      className={cn(
                        "flex items-center w-full transition-all duration-200 rounded-lg group",
                        collapsed ? "p-2 justify-center" : "px-3 py-2.5",
                        location.pathname === group.href
                          ? "bg-gradient-to-r from-white/10 to-white/5 text-white shadow-lg border border-white/10"
                          : "text-gray-300 hover:bg-gray-800/60 hover:text-white"
                      )}
                      title={collapsed ? group.group : undefined}
                    >
                      <GroupIcon className={cn(
                        "h-5 w-5 transition-colors flex-shrink-0",
                        location.pathname === group.href ? "text-white" : "text-gray-400 group-hover:text-gray-200"
                      )} />
                      {!collapsed && (
                        <span className="ml-3 text-xs font-medium">{group.group}</span>
                      )}
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Group Header - only show when expanded */}
                    {!collapsed && (
                      <div className="mb-1 mt-4 first:mt-1">
                        <div className="flex items-center w-full px-3 py-1.5">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 flex-1 text-left">{group.group}</span>
                        </div>
                      </div>
                    )}

                    {/* Group Items - show icons only when collapsed */}
                    {(isGroupExpanded || collapsed) && group.items && (
                      <div className={cn(collapsed ? "space-y-1" : "ml-2 space-y-1 pl-3 border-l border-gray-700/50")}>
                        {group.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.href;
                      const hasSubItems = item.subItems && item.subItems.length > 0;
                      const isItemExpanded = navSearchTerm ? true : expandedItems.includes(item.name);

                      return (
                        <div key={item.name}>
                          {/* Main Item */}
                          <button
                            onClick={() => {
                              if (hasSubItems && !collapsed) {
                                setExpandedItems(prev =>
                                  prev.includes(item.name)
                                    ? prev.filter(i => i !== item.name)
                                    : [...prev, item.name]
                                );
                              } else {
                                navigate(item.href);
                                setSidebarOpen?.(false);
                              }
                            }}
                            className={cn(
                              'group flex items-center w-full transition-all duration-200 rounded-lg relative',
                              collapsed ? "p-2 justify-center" : "px-3 py-2.5",
                              isActive
                                ? 'bg-gradient-to-r from-white/10 to-white/5 text-white shadow-lg border border-white/10'
                                : 'text-gray-300 hover:bg-gray-800/60 hover:text-white'
                            )}
                            title={collapsed ? item.name : undefined}
                          >
                            <Icon
                              className={cn(
                                'h-5 w-5 transition-colors flex-shrink-0',
                                isActive
                                  ? 'text-white'
                                  : 'text-gray-400 group-hover:text-gray-200'
                              )}
                            />
                            {!collapsed && (
                              <>
                                <span className="ml-3 flex-1 text-left text-xs font-medium">{item.name}</span>
                                {hasSubItems && (
                                  <ChevronRight
                                    className={cn(
                                      'h-4 w-4 transition-transform duration-300 text-gray-400',
                                      isItemExpanded ? 'rotate-90' : ''
                                    )}
                                  />
                                )}
                              </>
                            )}
                          </button>

                          {/* Sub Items - only when not collapsed */}
                          {!collapsed && hasSubItems && isItemExpanded && (
                            <div className="mt-1 ml-6 space-y-1 pl-3 border-l border-gray-700/30">
                              {item.subItems.map((subItem: any) => {
                                const isSubActive = location.pathname === subItem.href;
                                return (
                                  <button
                                    key={subItem.name}
                                    onClick={() => {
                                      navigate(subItem.href);
                                      setSidebarOpen?.(false);
                                    }}
                                    className={cn(
                                      'group flex items-center w-full px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200',
                                      isSubActive
                                        ? 'bg-gray-700/60 text-white'
                                        : 'text-gray-400 hover:bg-gray-700/40 hover:text-gray-200'
                                    )}
                                  >
                                    <div className={cn(
                                      "w-1.5 h-1.5 rounded-full mr-3 transition-colors",
                                      isSubActive ? "bg-white" : "bg-gray-500 group-hover:bg-gray-300"
                                    )}></div>
                                    <span className="flex-1 text-left">{subItem.name}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </nav>

        {/* Scroll fade indicator */}
        <div className={cn(
          "absolute bottom-0 left-0 right-0 h-20 pointer-events-none z-10 transition-opacity duration-500 flex items-end justify-center pb-2.5",
          hasMoreBelow && !collapsed ? "opacity-100" : "opacity-0"
        )}
          style={{ background: 'linear-gradient(to top, rgba(17,24,39,1) 0%, rgba(17,24,39,0.7) 50%, transparent 100%)' }}
        >
          <ChevronDown
            className="h-3.5 w-3.5 text-gray-500 animate-bounce"
            style={{ animationDuration: '2s' }}
          />
        </div>
        </div>

        {/* Pin sidebar button — desktop only */}
        {onTogglePin && (
          <div className={cn("hidden lg:flex border-t border-gray-800/60", collapsed ? "px-2 py-1 justify-center" : "px-3 py-1")}>
            <button
              onClick={onTogglePin}
              title={isPinned ? 'Soltar menu' : 'Fixar menu aberto'}
              className={cn(
                "flex items-center gap-2 rounded-lg transition-colors w-full",
                collapsed ? "p-1.5 justify-center" : "px-3 py-1.5",
                isPinned
                  ? "text-[#96bf13] hover:bg-gray-800/60"
                  : "text-gray-500 hover:text-gray-300 hover:bg-gray-800/60"
              )}
            >
              {isPinned ? <PinOff className="h-4 w-4 flex-shrink-0" /> : <Pin className="h-4 w-4 flex-shrink-0" />}
              {!collapsed && (
                <span className="text-xs font-medium">{isPinned ? 'Soltar menu' : 'Fixar menu'}</span>
              )}
            </button>
          </div>
        )}

        {/* User section */}
        <div className={cn(
          "border-t border-gray-700/50 bg-gradient-to-t from-gray-900/50 to-transparent relative",
          collapsed ? "p-1.5" : "p-2"
        )}>
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen?.(!userMenuOpen)}
              className={cn(
                "flex items-center w-full rounded-xl bg-gradient-to-r from-gray-800/80 to-gray-700/60 hover:from-gray-700/80 hover:to-gray-600/60 transition-all duration-200 border border-gray-700/30 shadow-lg",
                collapsed ? "p-1.5 justify-center" : "p-2"
              )}
              title={collapsed ? user?.name || 'Usuário' : undefined}
            >
              <div className="flex-shrink-0 relative">
                {profilePicture ? (
                  <img
                    src={profilePicture}
                    alt={user?.name || 'Usuário'}
                    className={cn(
                      "rounded-full object-cover border-2 border-white/10 shadow-inner",
                      collapsed ? "h-8 w-8" : "h-10 w-10"
                    )}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className={cn(
                    "rounded-full bg-gradient-to-r from-white/10 to-white/5 border border-white/10 flex items-center justify-center shadow-inner",
                    collapsed ? "h-8 w-8" : "h-10 w-10"
                  )}
                  style={{ display: profilePicture ? 'none' : 'flex' }}
                >
                  <span className={cn("font-bold text-white", collapsed ? "text-xs" : "text-sm")}>
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                {updateAvailable && (
                  <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-yellow-400 border-2 border-gray-800" />
                )}
              </div>
              {!collapsed && (
                <>
                  <div className="ml-3 flex-1 min-w-0 text-left">
                    <p className="text-sm font-semibold text-white truncate">{user?.name || 'Usuário'}</p>
                    <p className="text-xs text-gray-300 truncate">@{user?.username || user?.email?.split('@')[0] || 'usuario'}</p>
                  </div>
                  <ChevronDown className={cn(
                    "h-4 w-4 text-white/60 transition-transform duration-200",
                    userMenuOpen && "rotate-180"
                  )} />
                </>
              )}
            </button>

              {/* User dropdown menu */}
              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen?.(false)} />
                  <div className="fixed bottom-20 left-4 w-72 bg-gray-800 rounded-xl shadow-2xl border border-gray-700 z-50 overflow-hidden">
                    <button
                      onClick={() => {
                        navigate('/profile/user');
                        setUserMenuOpen?.(false);
                        setSidebarOpen?.(false);
                      }}
                      className="group flex w-full items-center px-4 py-3 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200"
                    >
                      <Settings className="mr-3 h-4 w-4 text-gray-400 group-hover:text-white" />
                      <span className="font-medium">Meu Perfil</span>
                    </button>
                    <button
                      onClick={() => {
                        setPersonalSettingsOpen(true);
                        setUserMenuOpen?.(false);
                      }}
                      className="group flex w-full items-center px-4 py-3 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200"
                    >
                      <Sliders className="mr-3 h-4 w-4 text-gray-400 group-hover:text-white" />
                      <span className="font-medium">Preferências</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/notifications');
                        setUserMenuOpen?.(false);
                        setSidebarOpen?.(false);
                      }}
                      className="group flex w-full items-center px-4 py-3 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200"
                    >
                      <Bell className="mr-3 h-4 w-4 text-gray-400 group-hover:text-white" />
                      <span className="font-medium">Notificações</span>
                    </button>
                    {operationalRoles.length > 1 && (
                      <button
                        onClick={() => {
                          openRolePicker();
                          setUserMenuOpen?.(false);
                          setSidebarOpen?.(false);
                        }}
                        className="group flex w-full items-center px-4 py-3 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200"
                      >
                        <ArrowLeftRight className="mr-3 h-4 w-4 text-gray-400 group-hover:text-white" />
                        <span className="font-medium">Trocar Perfil</span>
                      </button>
                    )}
                    <div className="border-t border-gray-700"></div>
                    {!!(window as any).electronAPI && (
                      <button
                        onClick={() => {
                          (window as any).electronAPI?.triggerUpdateCheck?.();
                          setUserMenuOpen?.(false);
                        }}
                        className="group flex w-full items-center px-4 py-3 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200"
                      >
                        <RefreshCw className="mr-3 h-4 w-4 text-gray-400 group-hover:text-white" />
                        <span className="font-medium">Verificar Atualizações</span>
                        {updateAvailable && (
                          <span className="ml-auto flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full bg-yellow-400" />
                            <span className="text-xs text-yellow-400 font-semibold">Novo</span>
                          </span>
                        )}
                      </button>
                    )}
                    {!!(window as any).electronAPI && (
                      <button
                        onClick={() => {
                          (window as any).electronAPI?.triggerReleaseNotes?.();
                          setUserMenuOpen?.(false);
                        }}
                        className="group flex w-full items-center px-4 py-3 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200"
                      >
                        <BookOpen className="mr-3 h-4 w-4 text-gray-400 group-hover:text-white" />
                        <span className="font-medium">Sobre</span>
                      </button>
                    )}
                    <button
                      onClick={() => {
                        navigate('/avisos-atualizacoes');
                        setUserMenuOpen?.(false);
                      }}
                      className="group flex w-full items-center px-4 py-3 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200"
                    >
                      <Megaphone className="mr-3 h-4 w-4 text-gray-400 group-hover:text-white" />
                      <span className="font-medium">Avisos e Atualizações</span>
                    </button>
                    <button
                      onClick={() => {
                        handleLogout();
                        setUserMenuOpen?.(false);
                      }}
                      className="group flex w-full items-center px-4 py-3 text-sm font-medium text-gray-300 hover:bg-gradient-to-r hover:from-red-800/60 hover:to-red-700/40 hover:text-white transition-all duration-200"
                    >
                      <LogOut className="mr-3 h-4 w-4 text-gray-400 group-hover:text-red-200" />
                      <span className="font-medium">Sair</span>
                    </button>
                  </div>
                </>
              )}
          </div>
        </div>
      </div>

      {/* Personal Settings Modal */}
      <PersonalSettingsModal
        open={personalSettingsOpen}
        onClose={() => setPersonalSettingsOpen(false)}
      />
    </div>
  );
};

export default Layout;
